/**
 * GRID-OS Digital Friction Check — scoring configuration
 * ---------------------------------------------------------------------------
 * Source of truth: Notion "GRID-OS Digital Friction Check — Final Master
 * Specification v1" (version v1_en_2026_08).
 *
 * This file contains ONLY data: questions, answers, points, area mapping,
 * bands, profiles and recommendations. It contains no DOM code, no network
 * code and no presentation logic. Content changes happen here and nowhere
 * else. Do not add rendering logic to this file.
 *
 * Answer point scale (spec §3):
 *   A = 0  Absent
 *   B = 2  Fragmented
 *   C = 5  Partially in place
 *   D = 8  Mostly aligned
 *   E = 10 Consistently working
 *
 * Each area is the sum of two questions, so area scores run 0–20 and the
 * overall score runs 0–100. All values are integers — there is no rounding.
 */

window.GridOsAssessmentConfig = (function () {
  'use strict';

  /**
   * One version across all languages. The language is carried separately in
   * `grid_os_language`, so a German completion and an English completion stay
   * comparable in the same report. Never fold the language back into VERSION.
   */
  var VERSION = 'v1_2026_08';
  var ASSESSMENT_NAME = 'GRID-OS Digital Friction Check';

  /* -------------------------------------------------------------------------
   * Localisation.
   *
   * English is the canonical layer and is never removed. A locale pack
   * (grid-os-assessment-strings-<locale>.js) only overlays DISPLAY strings.
   * Every English value that HubSpot or the result summary needs is preserved
   * on a `wire*` field before the overlay is applied, so translating a string
   * can never split a CRM report into one row per language.
   *
   * The pack must execute before this file. If it is missing, everything
   * falls back to English with no error.
   * ---------------------------------------------------------------------- */

  function resolveLocale() {
    var raw = window.GridOsLocale;
    if (!raw && document.documentElement) raw = document.documentElement.lang;
    raw = String(raw || 'en').toLowerCase();
    return raw.split('-')[0];
  }

  var PACK = null;
  var LANGUAGE = 'en';

  (function () {
    var locale = resolveLocale();
    var packs = window.GridOsAssessmentStrings;
    if (locale !== 'en' && packs && packs[locale]) {
      PACK = packs[locale];
      LANGUAGE = locale;
    }
  })();

  /* -------------------------------------------------------------------------
   * Answer scale
   * ---------------------------------------------------------------------- */

  var POINTS = { A: 0, B: 2, C: 5, D: 8, E: 10 };

  /* -------------------------------------------------------------------------
   * Areas (spec §3). Order here is the canonical display order and is also
   * used as the deterministic tie-breaker when two areas score equally.
   * ---------------------------------------------------------------------- */

  var AREAS = [
    {
      id: 'leadership',
      number: '01',
      name: 'Digital priorities & leadership',
      shortName: 'Leadership',
      hubspotScoreProperty: 'grid_os_leadership_score',
      hubspotStatusProperty: 'grid_os_leadership_status'
    },
    {
      id: 'website_ux',
      number: '02',
      name: 'Website, UX & conversion',
      shortName: 'Website & UX',
      hubspotScoreProperty: 'grid_os_website_ux_score',
      hubspotStatusProperty: 'grid_os_website_ux_status'
    },
    {
      id: 'discoverability',
      number: '03',
      name: 'SEO, content & discoverability',
      shortName: 'Discoverability',
      hubspotScoreProperty: 'grid_os_discoverability_score',
      hubspotStatusProperty: 'grid_os_discoverability_status'
    },
    {
      id: 'coordination',
      number: '04',
      name: 'Teams, agencies & ownership',
      shortName: 'Coordination',
      hubspotScoreProperty: 'grid_os_coordination_score',
      hubspotStatusProperty: 'grid_os_coordination_status'
    },
    {
      id: 'measurement',
      number: '05',
      name: 'Measurement & roadmap',
      shortName: 'Measurement & roadmap',
      hubspotScoreProperty: 'grid_os_measurement_roadmap_score',
      hubspotStatusProperty: 'grid_os_measurement_roadmap_status'
    }
  ];

  /* -------------------------------------------------------------------------
   * Area status bands (spec §3). Applied to the 0–20 area score.
   * `rank` is used for the recommendation lookup.
   * ---------------------------------------------------------------------- */

  var AREA_BANDS = [
    { id: 'critical',    rank: 0, min: 0,  max: 5,  label: 'Critical friction' },
    { id: 'fragmented',  rank: 1, min: 6,  max: 10, label: 'Fragmented' },
    { id: 'developing',  rank: 2, min: 11, max: 15, label: 'Developing' },
    { id: 'aligned',     rank: 3, min: 16, max: 20, label: 'Aligned' }
  ];

  /* -------------------------------------------------------------------------
   * The ten questions (spec §1 and §2).
   * `id` and `code` are stable identifiers. Never renumber or reuse them —
   * they are written to HubSpot and must stay comparable over time.
   * ---------------------------------------------------------------------- */

  var QUESTIONS = [
    {
      id: 'q01_priority_clarity',
      number: 1,
      area: 'leadership',
      hubspotProperty: 'grid_os_q01_priority_clarity',
      text: 'How clearly are your organisation’s digital priorities defined for the next 90 days?',
      answers: [
        { code: 'A', text: 'We do not have defined digital priorities.' },
        { code: 'B', text: 'Different teams have their own priorities, but there is no shared view.' },
        { code: 'C', text: 'We have general priorities, but they are not clearly ranked or time-bound.' },
        { code: 'D', text: 'We have agreed 90-day priorities, although some details or dependencies remain unclear.' },
        { code: 'E', text: 'We have a clearly ranked and communicated set of 90-day priorities used to guide decisions.' }
      ]
    },
    {
      id: 'q02_senior_ownership',
      number: 2,
      area: 'leadership',
      hubspotProperty: 'grid_os_q02_senior_ownership',
      text: 'Is one senior person clearly accountable for connecting digital priorities with execution?',
      answers: [
        { code: 'A', text: 'No one currently holds overall accountability.' },
        { code: 'B', text: 'Accountability is distributed and often unclear.' },
        { code: 'C', text: 'Someone informally coordinates the work but lacks clear authority or capacity.' },
        { code: 'D', text: 'A senior owner is defined, although some decisions or responsibilities remain distributed.' },
        { code: 'E', text: 'One senior owner has clear accountability, authority and visibility across strategy and execution.' }
      ]
    },
    {
      id: 'q03_website_clarity',
      number: 3,
      area: 'website_ux',
      hubspotProperty: 'grid_os_q03_website_clarity',
      text: 'How clearly does your website communicate what you offer, who it is for and why customers should choose you?',
      answers: [
        { code: 'A', text: 'The website no longer accurately represents our business.' },
        { code: 'B', text: 'Visitors can see what we do, but the target audience and value are unclear.' },
        { code: 'C', text: 'The main offer is understandable, but messaging is inconsistent or too broad.' },
        { code: 'D', text: 'The offer, audience and value are mostly clear, with some areas requiring improvement.' },
        { code: 'E', text: 'The website communicates a clear offer, audience and differentiated value across the customer journey.' }
      ]
    },
    {
      id: 'q04_conversion_visibility',
      number: 4,
      area: 'website_ux',
      hubspotProperty: 'grid_os_q04_conversion_visibility',
      text: 'How well can your team identify where website visitors hesitate, abandon or fail to convert?',
      answers: [
        { code: 'A', text: 'We do not currently measure or investigate conversion behaviour.' },
        { code: 'B', text: 'We have basic traffic data but little understanding of user behaviour.' },
        { code: 'C', text: 'We can identify some problem areas, but analysis is irregular or incomplete.' },
        { code: 'D', text: 'We monitor key journeys and can identify most significant conversion problems.' },
        { code: 'E', text: 'We consistently combine analytics, user evidence and testing to identify and resolve conversion friction.' }
      ]
    },
    {
      id: 'q05_commercial_alignment',
      number: 5,
      area: 'discoverability',
      hubspotProperty: 'grid_os_q05_commercial_alignment',
      text: 'How closely are your SEO and content activities connected to your commercial priorities?',
      answers: [
        { code: 'A', text: 'We have little or no active SEO and content programme.' },
        { code: 'B', text: 'Content is produced occasionally without clear commercial priorities.' },
        { code: 'C', text: 'Some activities support business goals, but planning and measurement are inconsistent.' },
        { code: 'D', text: 'Most SEO and content work is connected to defined audiences, offers and outcomes.' },
        { code: 'E', text: 'SEO and content are systematically prioritised, produced and measured against commercial objectives.' }
      ]
    },
    {
      id: 'q06_search_ai_visibility',
      number: 6,
      area: 'discoverability',
      hubspotProperty: 'grid_os_q06_search_ai_visibility',
      text: 'How clearly do you understand how potential customers discover your business through search engines and AI-generated answers?',
      answers: [
        { code: 'A', text: 'We have no visibility into how customers discover us through search or AI.' },
        { code: 'B', text: 'We know a few important search terms but do not track performance consistently.' },
        { code: 'C', text: 'We monitor organic search, but AI visibility and broader discovery paths remain unclear.' },
        { code: 'D', text: 'We understand our main search queries and have started assessing visibility in AI-generated answers.' },
        { code: 'E', text: 'We systematically monitor search demand, rankings, AI citations, brand representation and resulting conversions.' }
      ]
    },
    {
      id: 'q07_shared_priorities',
      number: 7,
      area: 'coordination',
      hubspotProperty: 'grid_os_q07_shared_priorities',
      text: 'To what extent are internal teams, agencies and suppliers working from the same priorities?',
      answers: [
        { code: 'A', text: 'Each team or supplier works independently.' },
        { code: 'B', text: 'Priorities are communicated informally and often interpreted differently.' },
        { code: 'C', text: 'Some teams share plans, but coordination is inconsistent across workstreams.' },
        { code: 'D', text: 'Most participants work from agreed priorities, although occasional conflicts or duplication remain.' },
        { code: 'E', text: 'Internal teams and external partners work from one prioritised roadmap with regular coordination.' }
      ]
    },
    {
      id: 'q08_responsibilities',
      number: 8,
      area: 'coordination',
      hubspotProperty: 'grid_os_q08_responsibilities',
      text: 'How clearly are ownership, decision rights and dependencies defined across your digital work?',
      answers: [
        { code: 'A', text: 'Responsibilities and decision rights are largely undefined.' },
        { code: 'B', text: 'People generally know their tasks, but ownership becomes unclear when decisions or dependencies arise.' },
        { code: 'C', text: 'Key responsibilities are documented, but gaps and overlaps still slow delivery.' },
        { code: 'D', text: 'Ownership and decision rights are mostly clear across active workstreams.' },
        { code: 'E', text: 'Owners, decision-makers, dependencies and escalation paths are explicit and consistently followed.' }
      ]
    },
    {
      id: 'q09_leadership_visibility',
      number: 9,
      area: 'measurement',
      hubspotProperty: 'grid_os_q09_leadership_visibility',
      text: 'How clearly can leadership see what digital work is progressing and what measurable results it is producing?',
      answers: [
        { code: 'A', text: 'Leadership has no consistent view of digital activity or results.' },
        { code: 'B', text: 'Updates focus mainly on completed tasks rather than outcomes.' },
        { code: 'C', text: 'Some metrics and progress updates exist, but they are fragmented or difficult to interpret.' },
        { code: 'D', text: 'Leadership receives regular reporting covering priorities, progress and key results.' },
        { code: 'E', text: 'Leadership has a clear, trusted view of progress, outcomes, blockers and decisions required.' }
      ]
    },
    {
      id: 'q10_roadmap',
      number: 10,
      area: 'measurement',
      hubspotProperty: 'grid_os_q10_roadmap',
      text: 'How complete is your 90-day digital roadmap in defining priorities, owners, timing and measurable outcomes?',
      answers: [
        { code: 'A', text: 'We do not have a 90-day digital roadmap.' },
        { code: 'B', text: 'We have a list of initiatives but no clear prioritisation, ownership or timing.' },
        { code: 'C', text: 'A roadmap exists, but some owners, dependencies, timings or outcomes are missing.' },
        { code: 'D', text: 'The roadmap defines most priorities, owners, timings and measurable outcomes.' },
        { code: 'E', text: 'We actively manage a prioritised 90-day roadmap that connects owners, dependencies, timing, KPIs and leadership decisions.' }
      ]
    }
  ];

  /* -------------------------------------------------------------------------
   * Area result copy (spec §4), keyed by area id then band id.
   * ---------------------------------------------------------------------- */

  var AREA_RESULTS = {
    leadership: {
      critical:   { title: 'No shared direction',        body: 'Priorities are absent or competing, with no sufficiently accountable senior owner.' },
      fragmented: { title: 'Distributed direction',      body: 'Some direction exists, but coordination and authority remain informal.' },
      developing: { title: 'Emerging alignment',         body: 'Priorities and ownership exist, with gaps in sequencing or communication.' },
      aligned:    { title: 'Clear digital leadership',   body: 'Shared priorities guide decisions through visible senior accountability.' }
    },
    website_ux: {
      critical:   { title: 'Unclear customer experience',    body: 'The offer and conversion problems are not reliably understood.' },
      fragmented: { title: 'Limited conversion visibility',  body: 'Parts of the experience work, but journeys and friction remain unclear.' },
      developing: { title: 'Developing website performance', body: 'Foundations are solid, with gaps in messaging, evidence or optimisation.' },
      aligned:    { title: 'Conversion-aware experience',    body: 'Clear value is supported by analytics, user evidence and testing.' }
    },
    discoverability: {
      critical:   { title: 'Low discoverability intelligence',      body: 'Search and AI visibility are poorly understood.' },
      fragmented: { title: 'Disconnected visibility activity',      body: 'Activity exists without consistent commercial alignment or measurement.' },
      developing: { title: 'Developing discoverability',            body: 'Visibility is monitored, with gaps in research, authority or conversion.' },
      aligned:    { title: 'Commercially aligned discoverability',  body: 'Search, content and AI visibility operate as one measured system.' }
    },
    coordination: {
      critical:   { title: 'Unowned delivery',          body: 'Unclear responsibilities create delays, duplication and stalled work.' },
      fragmented: { title: 'Fragmented coordination',   body: 'Responsibilities exist, but priorities and decisions remain inconsistent.' },
      developing: { title: 'Improving coordination',    body: 'Ownership mostly works, with remaining handover and dependency friction.' },
      aligned:    { title: 'Coordinated execution',     body: 'Teams share priorities, ownership and escalation paths.' }
    },
    measurement: {
      critical:   { title: 'Invisible progress',              body: 'Leadership cannot connect activity with outcomes or decisions.' },
      fragmented: { title: 'Activity-led reporting',          body: 'Updates focus on tasks instead of progress and blockers.' },
      developing: { title: 'Developing operational control',  body: 'Roadmap and reporting work, with some incomplete owners or KPIs.' },
      aligned:    { title: 'Measurable roadmap',              body: 'Priorities, owners, timing, outcomes and decisions are connected.' }
    }
  };

  /* -------------------------------------------------------------------------
   * Area recommendation library (spec §6), keyed by area id then band id.
   * ---------------------------------------------------------------------- */

  var AREA_RECOMMENDATIONS = {
    leadership: {
      critical:   'Define one accountable owner and 90-day direction',
      fragmented: 'Turn distributed priorities into one leadership view',
      developing: 'Connect priorities with execution',
      aligned:    'Protect clarity as execution scales'
    },
    website_ux: {
      critical:   'Diagnose the journey before another redesign',
      fragmented: 'Connect messaging with customer behaviour',
      developing: 'Move to continuous optimisation',
      aligned:    'Compound a strong experience with evidence'
    },
    discoverability: {
      critical:   'Establish how customers search before more content',
      fragmented: 'Connect SEO and content to commercial priorities',
      developing: 'Strengthen search and AI authority',
      aligned:    'Turn intelligence into a repeatable growth loop'
    },
    coordination: {
      critical:   'Clarify ownership before another supplier or workstream',
      fragmented: 'Create one coordination model',
      developing: 'Remove handover and dependency friction',
      aligned:    'Preserve coordination as initiatives grow'
    },
    measurement: {
      critical:   'Make priorities and progress visible in one roadmap',
      fragmented: 'Move from activity to outcome reporting',
      developing: 'Connect roadmap and reporting',
      aligned:    'Maintain focus as opportunities expand'
    }
  };

  /* -------------------------------------------------------------------------
   * Overall profiles (spec §5) and the profile-level recommendation (spec §6).
   * `scoreBand` is the only score representation sent to GA4.
   * ---------------------------------------------------------------------- */

  var PROFILES = [
    {
      id: 'fragmented',
      min: 0,
      max: 35,
      scoreBand: '0-35',
      label: 'Fragmented',
      headline: 'Digital work is happening, but the system around it is not yet connected.',
      body: 'Useful activity exists, but priorities, ownership, customer experience, discoverability and measurement do not operate as one system.',
      risk: 'More activity may increase complexity without creating proportionally greater progress.',
      recommendation: 'Create clarity before adding more digital activity.'
    },
    {
      id: 'partially_aligned',
      min: 36,
      max: 70,
      scoreBand: '36-70',
      label: 'Partially aligned',
      headline: 'Strong elements are in place, but alignment breaks between strategy and execution.',
      body: 'Important foundations work in parts of the business. The main constraint is inconsistency between teams, suppliers, channels and decisions.',
      risk: 'Good initiatives may underperform because dependencies and priorities remain only partially connected.',
      recommendation: 'Connect the strong elements already in place.'
    },
    {
      id: 'growth_ready',
      min: 71,
      max: 100,
      scoreBand: '71-100',
      label: 'Growth-ready',
      headline: 'Your digital foundations are strong enough to support focused, measurable growth.',
      body: 'Priorities, customer experience, teams and measurement are connected. The opportunity is to remove remaining constraints and scale without losing clarity.',
      risk: 'Growth can create friction if execution scales faster than ownership and measurement.',
      recommendation: 'Focus the system on the constraints that matter most.'
    }
  ];

  /**
   * Spec §3 override. A score of 71+ only earns "Growth-ready" if EVERY area
   * scores at least 6. If any area is in the critical band (0–5), the final
   * profile is downgraded to "Partially aligned" and the critical-friction
   * flag is raised. This is why HubSpot stores both grid_os_calculated_profile
   * (the raw band) and grid_os_final_profile (after this override).
   */
  var CRITICAL_OVERRIDE = {
    appliesToProfileId: 'growth_ready',
    downgradeToProfileId: 'partially_aligned',
    flagLabel: 'Critical Friction Detected',
    note: 'One or more areas show critical friction, which limits how far current strengths can carry the organisation.'
  };

  /** Spec §4: three or more critical areas raise a systemic-friction flag. */
  var SYSTEMIC_FRICTION_THRESHOLD = 3;

  /* -------------------------------------------------------------------------
   * Lead capture fields (spec §7 and landing copy "Contact step").
   * Only these fields are collected. Last name is deliberately not collected.
   * ---------------------------------------------------------------------- */

  var LEAD_FIELDS = [
    { id: 'firstname', hubspotProperty: 'firstname', label: 'First name',   type: 'text',  autocomplete: 'given-name',   required: true },
    { id: 'email',     hubspotProperty: 'email',     label: 'Work email',   type: 'email', autocomplete: 'email',        required: true },
    { id: 'company',   hubspotProperty: 'company',   label: 'Company name', type: 'text',  autocomplete: 'organization', required: true },
    { id: 'jobtitle',  hubspotProperty: 'jobtitle',  label: 'Job title',    type: 'text',  autocomplete: 'organization-title', required: false },
    { id: 'website',   hubspotProperty: 'website',   label: 'Company website', type: 'url', autocomplete: 'url',         required: false },
    {
      id: 'primary_priority',
      hubspotProperty: 'grid_os_primary_priority',
      label: 'Primary digital priority',
      type: 'select',
      required: false,
      options: [
        { value: '',                 label: 'Select if relevant' },
        { value: 'leadership',       label: 'Digital priorities & leadership' },
        { value: 'website_ux',       label: 'Website, UX & conversion' },
        { value: 'discoverability',  label: 'SEO, content & discoverability' },
        { value: 'coordination',     label: 'Teams, agencies & ownership' },
        { value: 'measurement',      label: 'Measurement & roadmap' }
      ]
    }
  ];

  /**
   * Marketing consent is optional and must never be pre-selected. Processing
   * the assessment itself is covered by the request the user has made, and is
   * stated in the privacy notice rather than gated behind a checkbox.
   */
  var CONSENT = {
    marketing: {
      id: 'marketing_consent',
      hubspotProperty: 'grid_os_marketing_consent',
      required: false,
      defaultChecked: false,
      label: 'Send me occasional practical insights on digital strategy, UX, discoverability and growth.'
    }
  };

  /* -------------------------------------------------------------------------
   * GDPR consent recorded with the submission.
   *
   * The portal has GDPR on and "send emails to contacts with a legal basis"
   * on. A submission that carries no legalConsentOptions creates a contact
   * HubSpot then refuses to email — the confirmation silently fails with
   * "there isn't GDPR-compliant legal basis for sending to them".
   *
   * The ids below are HubSpot subscription types:
   *   - results   : delivery of the assessment result. Not marketing.
   *   - marketing : optional, driven by the checkbox on the form.
   *
   * The text stored against each consent is deliberately the SAME string the
   * visitor reads on the form, taken from the localised UI table. Never invent
   * wording here: what is recorded must match what was shown.
   * ---------------------------------------------------------------------- */

  var LEGAL_CONSENT = {
    resultsSubscriptionTypeId: 3437738438,
    marketingSubscriptionTypeId: 606143343
  };

  /* -------------------------------------------------------------------------
   * Analytics event names (spec §8).
   * `friction_check_lead_error` is an addition, not in the spec: the brief
   * requires an integration-error event. It carries no personal data.
   * ---------------------------------------------------------------------- */

  var EVENTS = {
    ctaClick:        'friction_check_cta_click',
    view:            'friction_check_view',
    start:           'friction_check_start',
    progress:        'friction_check_progress',
    questionView:    'friction_check_question_view',
    complete:        'friction_check_complete',
    resultView:      'friction_check_result_view',
    leadFormView:    'friction_check_lead_form_view',
    generateLead:    'generate_lead',
    leadError:       'friction_check_lead_error',
    fullResultView:  'friction_check_full_result_view',
    scoreReview:     'score_review_click',
    schedulerView:   'meeting_scheduler_view',
    booked:          'alignment_call_booked',
    restart:         'friction_check_restart',
    stepBack:        'friction_check_step_back'
  };

  /**
   * Spec §8: progress fires after Q2, Q5 and Q8 only — three pings, not ten.
   * Keyed by the number of questions answered.
   */
  var PROGRESS_MILESTONES = { 2: 20, 5: 50, 8: 80 };

  /* -------------------------------------------------------------------------
   * Attribution. Only these query parameters are read, and every value is
   * length-capped and stripped of anything outside the safe character set
   * before it is stored or sent anywhere.
   * ---------------------------------------------------------------------- */

  var ATTRIBUTION_PARAMS = [
    { param: 'utm_source',        hubspotProperty: 'grid_os_utm_source' },
    { param: 'utm_medium',        hubspotProperty: 'grid_os_utm_medium' },
    { param: 'utm_campaign',      hubspotProperty: 'grid_os_utm_campaign' },
    { param: 'utm_content',       hubspotProperty: 'grid_os_utm_content' },
    { param: 'utm_term',          hubspotProperty: 'grid_os_utm_term' },
    { param: 'assessment_source', hubspotProperty: 'grid_os_source_type' },
    { param: 'source_page',       hubspotProperty: 'grid_os_source_name' },
    { param: 'content_id',        hubspotProperty: 'grid_os_content_id' }
  ];
  /* NOTE: content_id must NOT map to grid_os_source_url. The submission also
     writes the current page URL to grid_os_source_url, so sharing the property
     would put the same field in the payload twice with different values. */

  var ATTRIBUTION_MAX_LENGTH = 255;
  var ATTRIBUTION_ALLOWED = /[^a-zA-Z0-9\-_.:/? =+%&,]/g;

  /* -------------------------------------------------------------------------
   * Interface strings.
   *
   * Everything the visitor reads that is not question, answer, area, profile
   * or recommendation copy. Placeholders in {braces} are substituted at
   * render time — never build these by concatenation, because word order and
   * plural rules differ by language.
   * ---------------------------------------------------------------------- */

  var UI = {
    questionProgress: 'Question {number} of {total}',
    questionAnnounce: 'Question {number} of {total}. {text}',
    back: 'Back',
    next: 'Next',
    seeScore: 'See my score',
    answerRequired: 'Select the statement that best reflects how your organisation works today.',
    answerRequiredAnnounce: 'No answer selected. Choose one statement to continue.',
    scoreHeading: 'Your GRID-OS Digital Alignment Score',
    previewAnnounce: 'Your score is {score} out of 100. Profile: {profile}. Enter your details to see the full five-area breakdown.',
    optionalSuffix: ' (optional)',
    fieldRequired: '{label} is required.',
    emailInvalid: 'Enter a valid email address.',
    emailFreeMail: 'Please use your work email address.',
    formErrorOne: 'There is one problem with the form: {problem}',
    formErrorMany: 'There are {count} problems with the form. Please review the fields marked below.',
    privacyBody: 'Your information will be used to provide the assessment result and manage your request. Marketing communication is optional and requires separate consent. ',
    privacyLink: 'See our Privacy Policy.',
    sending: 'Sending…',
    sendingAnnounce: 'Sending your details.',
    submitFailed: 'We could not send your details just now. Your score has not been lost — select “{label}” to try again.',
    submitFailedAnnounce: 'Submission failed. Your result is safe. You can try again.',
    riskLabel: 'Risk: ',
    areasHeading: 'Your five areas',
    strongestLabel: 'Your strongest area',
    primaryFrictionLabel: 'Your primary friction area',
    secondaryFrictionLabel: 'Your secondary friction area',
    tiedNote: 'Tied with at least one other area.',
    evenNote: 'Your five areas score evenly. That points to a consistent operating pattern rather than one isolated constraint, so sequencing matters more than picking a single area.',
    prioritiesHeading: 'Your first three priorities',
    startHereLabel: 'Start here: ',
    restart: 'Start the assessment again',
    restartAnnounce: 'Assessment reset. You can start again.',
    disclaimer: 'The GRID-OS Digital Friction Check provides a directional assessment based on the information submitted. It is not legal, financial, security or technical advice and does not replace a formal audit.',
    fullResultAnnounce: 'Full result ready. Score {score} out of 100, profile {profile}. Primary friction area: {area}.'
  };

  /**
   * English recommendation copy, preserved before the overlay. The result
   * summary written to HubSpot reads from here so that a sales briefing is
   * always in one language regardless of what the visitor saw.
   */
  var WIRE_AREA_RECOMMENDATIONS = {};

  function applyStrings() {
    var pack = PACK || {};
    var i, j;

    /* Areas ------------------------------------------------------------- */
    for (i = 0; i < AREAS.length; i++) {
      var area = AREAS[i];
      area.wireName = area.name;
      area.wireShortName = area.shortName;
      var ap = pack.areas && pack.areas[area.id];
      if (ap) {
        if (ap.name) area.name = ap.name;
        if (ap.shortName) area.shortName = ap.shortName;
      }
    }

    /* Bands ------------------------------------------------------------- */
    for (i = 0; i < AREA_BANDS.length; i++) {
      var band = AREA_BANDS[i];
      band.wireLabel = band.label;
      var bp = pack.areaBands && pack.areaBands[band.id];
      if (bp && bp.label) band.label = bp.label;
    }

    /* Questions and answers. Codes are wire values and never change. ----- */
    for (i = 0; i < QUESTIONS.length; i++) {
      var q = QUESTIONS[i];
      var qp = pack.questions && pack.questions[q.id];
      if (!qp) continue;
      if (qp.text) q.text = qp.text;
      if (qp.answers) {
        for (j = 0; j < q.answers.length; j++) {
          var answerText = qp.answers[q.answers[j].code];
          if (answerText) q.answers[j].text = answerText;
        }
      }
    }

    /* Area result copy — shown on screen only, never sent anywhere. ------ */
    for (var areaId in AREA_RESULTS) {
      if (!Object.prototype.hasOwnProperty.call(AREA_RESULTS, areaId)) continue;
      var rp = pack.areaResults && pack.areaResults[areaId];
      if (!rp) continue;
      for (var bandId in AREA_RESULTS[areaId]) {
        if (!Object.prototype.hasOwnProperty.call(AREA_RESULTS[areaId], bandId)) continue;
        if (!rp[bandId]) continue;
        if (rp[bandId].title) AREA_RESULTS[areaId][bandId].title = rp[bandId].title;
        if (rp[bandId].body) AREA_RESULTS[areaId][bandId].body = rp[bandId].body;
      }
    }

    /* Recommendations — English kept for the summary. -------------------- */
    for (var recAreaId in AREA_RECOMMENDATIONS) {
      if (!Object.prototype.hasOwnProperty.call(AREA_RECOMMENDATIONS, recAreaId)) continue;
      WIRE_AREA_RECOMMENDATIONS[recAreaId] = {};
      var recPack = pack.areaRecommendations && pack.areaRecommendations[recAreaId];
      for (var recBandId in AREA_RECOMMENDATIONS[recAreaId]) {
        if (!Object.prototype.hasOwnProperty.call(AREA_RECOMMENDATIONS[recAreaId], recBandId)) continue;
        WIRE_AREA_RECOMMENDATIONS[recAreaId][recBandId] = AREA_RECOMMENDATIONS[recAreaId][recBandId];
        if (recPack && recPack[recBandId]) {
          AREA_RECOMMENDATIONS[recAreaId][recBandId] = recPack[recBandId];
        }
      }
    }

    /* Profiles ---------------------------------------------------------- */
    for (i = 0; i < PROFILES.length; i++) {
      var profile = PROFILES[i];
      profile.wireLabel = profile.label;
      profile.wireRecommendation = profile.recommendation;
      var pp = pack.profiles && pack.profiles[profile.id];
      if (!pp) continue;
      if (pp.label) profile.label = pp.label;
      if (pp.headline) profile.headline = pp.headline;
      if (pp.body) profile.body = pp.body;
      if (pp.risk) profile.risk = pp.risk;
      if (pp.recommendation) profile.recommendation = pp.recommendation;
    }

    /* Critical override ------------------------------------------------- */
    if (pack.criticalOverride) {
      if (pack.criticalOverride.flagLabel) CRITICAL_OVERRIDE.flagLabel = pack.criticalOverride.flagLabel;
      if (pack.criticalOverride.note) CRITICAL_OVERRIDE.note = pack.criticalOverride.note;
    }

    /* Lead fields. Option VALUES are wire ids and are never translated. -- */
    for (i = 0; i < LEAD_FIELDS.length; i++) {
      var field = LEAD_FIELDS[i];
      var fp = pack.leadFields && pack.leadFields[field.id];
      if (!fp) continue;
      if (fp.label) field.label = fp.label;
      if (field.options && fp.options) {
        for (j = 0; j < field.options.length; j++) {
          var optionLabel = fp.options[field.options[j].value];
          if (optionLabel) field.options[j].label = optionLabel;
        }
      }
    }

    /* Consent ----------------------------------------------------------- */
    if (pack.consent && pack.consent.marketing && pack.consent.marketing.label) {
      CONSENT.marketing.label = pack.consent.marketing.label;
    }

    /* Interface --------------------------------------------------------- */
    if (pack.ui) {
      for (var uiKey in pack.ui) {
        if (!Object.prototype.hasOwnProperty.call(pack.ui, uiKey)) continue;
        if (pack.ui[uiKey]) UI[uiKey] = pack.ui[uiKey];
      }
    }
  }

  applyStrings();

  /* -------------------------------------------------------------------------
   * sessionStorage key. Deliberately session-scoped: answers are cleared when
   * the tab closes, on successful completion, and on explicit restart.
   * Personal data is never written here. The language is part of the key so
   * switching language mid-session cannot mix two sets of answers.
   * ---------------------------------------------------------------------- */

  var STORAGE_KEY = 'gridos_dfc_' + VERSION + '_' + LANGUAGE;

  return {
    version: VERSION,
    language: LANGUAGE,
    assessmentName: ASSESSMENT_NAME,
    points: POINTS,
    areas: AREAS,
    areaBands: AREA_BANDS,
    questions: QUESTIONS,
    areaResults: AREA_RESULTS,
    areaRecommendations: AREA_RECOMMENDATIONS,
    wireAreaRecommendations: WIRE_AREA_RECOMMENDATIONS,
    profiles: PROFILES,
    criticalOverride: CRITICAL_OVERRIDE,
    systemicFrictionThreshold: SYSTEMIC_FRICTION_THRESHOLD,
    leadFields: LEAD_FIELDS,
    consent: CONSENT,
    legalConsent: LEGAL_CONSENT,
    ui: UI,
    events: EVENTS,
    progressMilestones: PROGRESS_MILESTONES,
    attributionParams: ATTRIBUTION_PARAMS,
    attributionMaxLength: ATTRIBUTION_MAX_LENGTH,
    attributionAllowed: ATTRIBUTION_ALLOWED,
    storageKey: STORAGE_KEY
  };
})();
